import { useState, useRef } from "react";
import { theme, fonts } from "../theme";

// ─── SPARKLINE (mini) ───────────────────────────────────────
export function Sparkline({ data, color, width = 200, height = 40 }) {
  if (!data || data.length < 2) return null;
  const c = color || theme.green;
  const mn = Math.min(...data), mx = Math.max(...data), rng = mx - mn || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height * 0.92 - ((v - mn) / rng) * height * 0.82}`).join(" ");
  const id = "sp" + Math.random().toString(36).slice(2, 8);
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c} stopOpacity="0.15" /><stop offset="100%" stopColor={c} stopOpacity="0" /></linearGradient></defs>
      <polygon points={`${pts} ${width},${height} 0,${height}`} fill={`url(#${id})`} />
      <polyline points={pts} fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── FULL EQUITY CHART (with tooltip) ────────────────────────
export default function EquityChart({ data, width: W = 720, height: H = 185 }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);

  if (!data || data.length < 2) {
    return <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: theme.text4, fontSize: 13 }}>No equity data available</div>;
  }

  const P = { t: 16, r: 16, b: 26, l: 54 };
  const iw = W - P.l - P.r, ih = H - P.t - P.b;
  const mn = Math.min(...data), mx = Math.max(...data), rng = mx - mn || 1;
  const pts = data.map((v, i) => [P.l + (i / (data.length - 1)) * iw, P.t + ih - ((v - mn) / rng) * ih]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
  const grids = [0, 0.25, 0.5, 0.75, 1].map(p => ({ y: P.t + ih - p * ih, label: "$" + (mn + p * rng).toFixed(0) }));
  const last = pts[pts.length - 1];
  const isPositive = data[data.length - 1] >= data[0];
  const strokeColor = isPositive ? "#16A34A" : "#DC2626";

  const onMove = e => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const sx = (e.clientX - rect.left) / rect.width * W;
    const idx = Math.round(((sx - P.l) / iw) * (data.length - 1));
    if (idx >= 0 && idx < data.length) {
      setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, val: data[idx], idx });
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      {tip && <div style={{ position: "absolute", left: tip.x + 12, top: tip.y - 36, background: theme.text, color: "#fff", padding: "5px 10px", borderRadius: 6, fontSize: 10, fontFamily: fonts.mono, pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
        ${tip.val?.toLocaleString(undefined, { minimumFractionDigits: 0 })} <span style={{ color: "rgba(255,255,255,0.5)" }}>#{tip.idx + 1}</span>
      </div>}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs><linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={strokeColor} stopOpacity="0.10" /><stop offset="100%" stopColor={strokeColor} stopOpacity="0" /></linearGradient></defs>
        {grids.map((g, i) => <g key={i}><line x1={P.l} y1={g.y} x2={W - P.r} y2={g.y} stroke="rgba(0,0,0,0.04)" strokeWidth="1" /><text x={P.l - 8} y={g.y + 3.5} textAnchor="end" fill={theme.text4} fontSize="10" fontFamily={fonts.mono}>{g.label}</text></g>)}
        <path d={d + ` L${last[0]},${P.t + ih} L${P.l},${P.t + ih} Z`} fill="url(#eqFill)" />
        <path d={d} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last[0]} cy={last[1]} r="4" fill={strokeColor} stroke="#fff" strokeWidth="2.5" />
        <circle cx={last[0]} cy={last[1]} r="8" fill="none" stroke={strokeColor} strokeWidth="1" opacity="0.2" style={{ animation: "pulse 2.5s infinite" }} />
      </svg>
    </div>
  );
}

// ─── BAR CHART ──────────────────────────────────────────────
export function BarChart({ data, labels, height = 80 }) {
  if (!data || data.length === 0) return null;
  const mx = Math.max(...data.map(Math.abs)) || 1;
  return (
    <svg width="100%" viewBox={`0 0 ${data.length * 42} ${height + 20}`} style={{ display: "block" }}>
      {data.map((v, i) => {
        const neg = v < 0;
        const bh = (Math.abs(v) / mx) * height * 0.68;
        const y = neg ? height / 2 : height / 2 - bh;
        return (
          <g key={i}>
            <rect x={i * 42 + 10} y={y} width={22} height={Math.max(bh, 2)} rx={4} fill={neg ? theme.red : theme.green} opacity={neg ? 0.55 : 0.6}>
              <title>${Math.round(v)}</title>
            </rect>
            {labels && labels[i] && <text x={i * 42 + 21} y={height + 15} textAnchor="middle" fill={theme.text4} fontSize="9" fontFamily={fonts.mono}>{labels[i]}</text>}
          </g>
        );
      })}
      <line x1="0" y1={height / 2} x2={data.length * 42} y2={height / 2} stroke={theme.border} strokeWidth="1" />
    </svg>
  );
}
